import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { COMPANY_INFO, NAV_LINKS } from '../data/content';

export function Footer() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [footerLinks, setFooterLinks] = useState(NAV_LINKS);

  useEffect(() => {
    async function fetchFooterLinks() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/menu_items');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            const sorted = items
              .filter((item) => item.active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item) => ({ label: item.title, path: item.path }));
            if (sorted.length > 0) {
              setFooterLinks(sorted);
            }
          }
        }
      } catch (err) {
        console.error('Kunne ikke hente menu_items til footer:', err);
      }
    }
    fetchFooterLinks();
  }, []);
  return (
    <footer className="bg-[#0D0D0D] text-slate-300 border-t border-[#283337]">
      {/* Top Footer Banner - Only on Home page */}
      {isHomePage && (
        <div className="bg-white border-b border-slate-200 py-8 px-4 sm:px-6 lg:px-8 text-slate-800">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="bg-[#5A6E75]/10 border border-[#5A6E75]/30 text-[#5A6E75] p-3 rounded-xl text-xl">
                <i className="fa-solid fa-award" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-slate-900 font-bold text-lg">
                  Dansk Eneforhandler af SYSTEM 3E
                </h3>
                <p className="text-slate-600 text-sm">
                  AGT Nordic Group ApS rådgiver og leverer direkte til private bygherrer og professionelle håndværkere.
                </p>
              </div>
            </div>
            <NavLink
              to="/kontakt"
              className="whitespace-nowrap bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-6 py-3 rounded-lg text-sm transition-all shadow-md shadow-[#122a4e]/20"
            >
              Kontakt os i dag
            </NavLink>
          </div>
        </div>
      )}

      {/* Main Footer Links - White Background */}
      <div className="bg-white text-slate-800 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Col 1: Brand & Sole Distributor */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/assets/6db9bc06-2d1f-414d-89eb-eaf682030584/3elogo.png"
                alt="SYSTEM 3E Logo"
                className="h-7 w-auto object-contain"
              />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              Revolutionerende modulært vægsystem baseret på perlite. Click-Click samling uden traditionel mørtel, lim eller vand for hurtigere og enklere byggeri.
            </p>
            <div className="inline-block bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-md text-xs font-semibold text-[#122a4e]">
              <i className="fa-solid fa-certificate mr-1.5 text-[#446ba9]" aria-hidden="true" />
              {COMPANY_INFO.soleDistributorText}
            </div>
          </div>

          {/* Col 2: Navigation Links */}
          <div>
            <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#122a4e] pl-2">
              Navigation
            </h4>
            <ul className="space-y-2 text-sm">
              {footerLinks.slice(0, 4).map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    className="text-slate-700 hover:text-[#122a4e] transition-colors flex items-center gap-2 font-medium"
                  >
                    <i className="fa-solid fa-chevron-right text-[10px] text-[#446ba9]" aria-hidden="true" />
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Target Groups */}
          <div>
            <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#122a4e] pl-2">
              Målgrupper & Sider
            </h4>
            <ul className="space-y-2 text-sm">
              {footerLinks.slice(4).map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    className="text-slate-700 hover:text-[#122a4e] transition-colors flex items-center gap-2 font-medium"
                  >
                    <i className="fa-solid fa-chevron-right text-[10px] text-[#446ba9]" aria-hidden="true" />
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Contact Information */}
          <div>
            <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#122a4e] pl-2">
              Kontaktoplysninger
            </h4>
            <div className="space-y-3 text-sm">
              <p className="font-bold text-slate-900">{COMPANY_INFO.name}</p>
              <p className="text-slate-600 flex items-start gap-2">
                <i className="fa-solid fa-location-dot text-[#446ba9] mt-1" aria-hidden="true" />
                <span>
                  {COMPANY_INFO.zipCity}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-[#446ba9]" aria-hidden="true" />
                <a
                  href={COMPANY_INFO.phoneRaw}
                  className="text-slate-800 hover:text-[#122a4e] transition-colors font-semibold whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-[#446ba9]" aria-hidden="true" />
                <a
                  href={COMPANY_INFO.emailRaw}
                  className="text-slate-800 hover:text-[#122a4e] transition-colors font-semibold whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal / Copyright Bar */}
      <div className="border-t border-slate-900 py-6 px-4 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>
            © {new Date().getFullYear()} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.
          </p>
          <p className="text-slate-500">
            SYSTEM 3E™ – Officiel dansk importør og eneforhandler.
          </p>
        </div>
      </div>
    </footer>
  );
}
